// ── A run waits its turn (`EX-SRN01`, `docs/07` "Runs wait their turn") ──
// Wave 34 decision 12. Every run route (practice, contest, exam, course) is
// asked with `?wait=lane`: the API then holds a run that finds no free share
// of the judge in its run lane instead of refusing it, and answers within
// twenty seconds either way. A finished run is the route's usual result; one
// still waiting or running is `{ runLane: { ticket, ... } }`, read again here
// until it finishes. The page shows "Running" the whole time and never the
// word busy; only a run that waited a full minute comes back as
// `JUDGE_ENGINE_BUSY`, which the pages word as "taking longer than usual".

import { ApiError, fetcher } from "@/lib/api";

type RunLaneTicket = { ticket: string; state: "waiting" | "running"; waitedMs: number };
type LaneAnswer<T> = T | { runLane: RunLaneTicket };

/** A short pause before reading again after a dropped connection. */
const RETRY_PAUSE_MS = 1_000;
/** Dropped connections in a row before the run is given up on. */
const RETRY_LIMIT = 3;

/** The route with `wait=lane` added, whatever query it already carries. */
export function laneRoute(path: string): string {
  return `${path}${path.includes("?") ? "&" : "?"}wait=lane`;
}

function ticketOf<T>(answer: LaneAnswer<T>): RunLaneTicket | null {
  if (!answer || typeof answer !== "object" || !("runLane" in answer)) return null;
  const lane = (answer as { runLane?: unknown }).runLane;
  if (!lane || typeof lane !== "object") return null;
  const ticket = (lane as { ticket?: unknown }).ticket;
  return typeof ticket === "string" ? (lane as RunLaneTicket) : null;
}

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Sends a run and follows it through the lane to its result.
 *
 * `send` is the page's POST with {@link laneRoute}. A ticket the API no longer
 * knows (it restarted, or the result was already read) and a connection that
 * keeps dropping both end as `JUDGE_ENGINE_BUSY`, so every page shows the one
 * calm "try again" it already has for a run that waited too long.
 */
export async function runThroughLane<T>(
  send: () => Promise<LaneAnswer<T>>,
): Promise<T> {
  let answer: LaneAnswer<T> = await send();
  let dropped = 0;
  for (let lane = ticketOf(answer); lane; lane = ticketOf(answer)) {
    try {
      const res = await fetcher<LaneAnswer<T>>(`/v1/judge/run-tickets/${encodeURIComponent(lane.ticket)}`);
      if (!res.success) throw new ApiError("The run could not be read.", 500);
      answer = res.data;
      dropped = 0;
    } catch (err) {
      if (err instanceof ApiError && err.code === "JUDGE_RUN_TICKET_UNKNOWN") {
        throw new ApiError(err.message, 503, "JUDGE_ENGINE_BUSY", undefined, { reason: "run_ticket_lost" });
      }
      // A dropped connection is not the run's answer: read the same ticket
      // again, a few times, before giving up on it.
      if (!(err instanceof ApiError) && dropped < RETRY_LIMIT) {
        dropped += 1;
        await pause(RETRY_PAUSE_MS);
        continue;
      }
      if (!(err instanceof ApiError)) {
        throw new ApiError("The run could not be read.", 503, "JUDGE_ENGINE_BUSY", undefined, { reason: "run_ticket_lost" });
      }
      throw err;
    }
  }
  return answer as T;
}

/** A newer run from the same person took this one's place in line: not a failure to show. */
export function isRunReplaced(err: unknown): boolean {
  return err instanceof ApiError && err.code === "JUDGE_RUN_REPLACED";
}
